import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Globe, X } from 'lucide-react';
import { useChatContextActions, useChatContexts } from './hooks';

const ICON_BY_TYPE = {
    insight: FileText,
    page: Globe,
    portfolio: FileText,
};

function ContextChip({ item, onRemove, onNavigate }) {
    const Icon = ICON_BY_TYPE[item.type] || FileText;
    const clickable = !!item.link && !!item.label;
    const isLoading = !item.label;

    if (isLoading) {
        return (
            <div className="inline-flex items-center gap-1 h-6 pl-1.5 pr-2 rounded-full bg-gray-100 border border-gray-200 text-[11px] max-w-[180px]">
                <Icon className="w-3 h-3 flex-shrink-0 text-gray-400" />
                <span className="block w-24 h-2.5 rounded-full bg-gray-200 animate-pulse" />
            </div>
        );
    }

    return (
        <div
            title={`${item.type}: ${item.label || item.uri}`}
            className="inline-flex items-center gap-1 h-6 pl-1.5 pr-0.5 rounded-full bg-gray-100 border border-gray-200 text-gray-700 text-[11px] max-w-[180px]"
        >
            <Icon className="w-3 h-3 flex-shrink-0 text-gray-500" />
            <button
                type="button"
                onClick={() => clickable && onNavigate(item.link)}
                className={`truncate focus:outline-none ${clickable ? 'hover:underline' : 'cursor-default'}`}
            >
                {item.label}
            </button>
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    onRemove(item.key);
                }}
                className="p-0.5 rounded-full hover:bg-gray-200 transition-colors"
                aria-label="Remove context"
            >
                <X className="w-2.5 h-2.5" />
            </button>
        </div>
    );
}

export default function ChatContextChipRow() {
    const items = useChatContexts();
    const { remove } = useChatContextActions();
    const navigate = useNavigate();

    if (!items || items.length === 0) return null;

    return (
        <div className="flex flex-wrap items-center gap-1.5 pb-2">
            {items.map((item) => (
                <ContextChip
                    key={item.key}
                    item={item}
                    onRemove={remove}
                    onNavigate={(link) => {
                        if (/^https?:\/\//.test(link)) {
                            window.open(link, '_blank', 'noopener');
                        } else {
                            navigate(link);
                        }
                    }}
                />
            ))}
        </div>
    );
}
